/* LuxeLane v2 product page behaviour (reference: product.html script).
 * Variant option cards -> matching variant (price, availability, URL,
 * image), gallery thumbnails, quantity stepper, accordions, AJAX add to
 * cart via window.LxCart (lx-motion.js, falls back to a normal form post),
 * and the mobile sticky buy bar.
 */
(() => {
  const FLASH_MS = 1400;

  function initProduct(root) {
    if (!root || root.dataset.lxReady) return;
    root.dataset.lxReady = '1';

    const data = JSON.parse(root.querySelector('[data-lx-product-json]')?.textContent || '{"variants":[]}');
    const form = root.querySelector('[data-lx-form]');
    const idInput = root.querySelector('[data-lx-variant-id]');
    const addBtn = root.querySelector('[data-lx-add]');
    const priceEl = root.querySelector('[data-lx-price]');
    const compareEl = root.querySelector('[data-lx-compare]');
    const stickyPrice = root.querySelector('[data-lx-sticky-price]');
    const stickyVariant = root.querySelector('[data-lx-sticky-variant]');
    const stickyAdd = root.querySelector('[data-lx-sticky-add]');
    const errorEl = root.querySelector('[data-lx-error]');
    const mainImg = root.querySelector('[data-lx-main-img]');
    const thumbs = [...root.querySelectorAll('[data-lx-thumb]')];
    const optionSets = [...root.querySelectorAll('[data-lx-option]')];

    /* ---------- Gallery ---------- */
    const SWAP_MS = 180;
    function swapMain(src, srcset) {
      if (!mainImg || !src) return;
      mainImg.classList.add('is-swapping');
      setTimeout(() => {
        if (srcset) mainImg.srcset = srcset;
        else mainImg.removeAttribute('srcset');
        mainImg.src = src;
        mainImg.classList.remove('is-swapping');
      }, SWAP_MS);
    }
    function showMedia(thumb) {
      if (!thumb) return;
      thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
      swapMain(thumb.dataset.src, thumb.dataset.srcset);
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    thumbs.forEach((t) => t.addEventListener('click', () => showMedia(t)));

    /* ---------- Variants ---------- */
    const selected = () => optionSets.map((set) => set.querySelector('.lx-variant.is-active')?.dataset.value);

    function findVariant(values) {
      return data.variants.find((v) => v.options.every((opt, i) => opt === values[i]));
    }

    function markAvailability(values) {
      optionSets.forEach((set, index) => {
        set.querySelectorAll('.lx-variant').forEach((btn) => {
          const trial = [...values];
          trial[index] = btn.dataset.value;
          const match = findVariant(trial);
          btn.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    // Blocks can be removed in the editor, so every element is optional.
    function applyVariant(variant) {
      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
        }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.price;
      if (compareEl) {
        compareEl.hidden = !variant.compare;
        compareEl.textContent = variant.compare || '';
      }
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? `Add to bag — ${variant.price}` : 'Sold out';
      }
      if (stickyPrice) stickyPrice.textContent = variant.price;
      if (stickyVariant) stickyVariant.textContent = variant.options.join(' / ');
      if (stickyAdd) {
        stickyAdd.disabled = !variant.available;
        stickyAdd.textContent = variant.available ? 'Add to bag' : 'Sold out';
      }
      if (variant.media) {
        const tile = thumbs.find((t) => Number(t.dataset.mediaId) === variant.media);
        if (tile) {
          thumbs.forEach((t) => t.classList.toggle('is-active', t === tile));
          swapMain(tile.dataset.src, tile.dataset.srcset);
        } else {
          thumbs.forEach((t) => t.classList.remove('is-active'));
          swapMain(variant.src, null);
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    optionSets.forEach((set) => {
      set.addEventListener('click', (event) => {
        const btn = event.target.closest('.lx-variant');
        if (!btn) return;
        set.querySelectorAll('.lx-variant').forEach((b) => {
          b.classList.toggle('is-active', b === btn);
          b.setAttribute('aria-pressed', String(b === btn));
        });
        const label = set.querySelector('[data-lx-option-value]');
        if (label) label.textContent = btn.dataset.value;
        const values = selected();
        markAvailability(values);
        applyVariant(findVariant(values));
      });
    });
    if (optionSets.length) markAvailability(selected());

    /* ---------- Selling plans ---------- */
    const plans = root.querySelector('[data-lx-plans]');
    const planInput = root.querySelector('[data-lx-plan-id]');
    plans?.addEventListener('click', (event) => {
      const btn = event.target.closest('.lx-variant');
      if (!btn) return;
      plans.querySelectorAll('.lx-variant').forEach((b) => {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-pressed', String(b === btn));
      });
      if (planInput) planInput.value = btn.dataset.plan || '';
    });

    /* ---------- Quantity ---------- */
    const qtyInput = root.querySelector('[data-lx-qty-input]');
    root.querySelectorAll('[data-lx-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (qtyInput) qtyInput.value = Math.max(1, (Number(qtyInput.value) || 1) + Number(btn.dataset.lxQty));
      });
    });

    /* ---------- Add to cart ---------- */
    async function add(sourceBtn) {
      if (!form || !idInput) return;
      if (!window.LxCart) {
        form.submit();
        return;
      }
      const label = sourceBtn.textContent;
      if (errorEl) errorEl.hidden = true;
      sourceBtn.disabled = true;
      try {
        await window.LxCart.addToCart(idInput.value, qtyInput?.value || 1, planInput?.value || null);
        sourceBtn.textContent = 'Added ✓';
        sourceBtn.style.background = 'var(--lx-green-deep)';
        setTimeout(() => {
          sourceBtn.textContent = label;
          sourceBtn.style.background = '';
          sourceBtn.disabled = false;
        }, FLASH_MS);
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
        sourceBtn.textContent = label;
        sourceBtn.disabled = false;
      }
    }
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      add(addBtn);
    });
    root.querySelector('[data-lx-sticky-add]')?.addEventListener('click', (event) => {
      if (!addBtn || addBtn.disabled) return;
      add(event.currentTarget);
    });

    /* ---------- Accordions (one open at a time) ---------- */
    const accs = [...root.querySelectorAll('[data-lx-acc]')];
    accs.forEach((acc) => {
      acc.querySelector('.lx-acc__head').addEventListener('click', () => {
        const open = !acc.classList.contains('is-open');
        accs.forEach((other) => {
          other.classList.toggle('is-open', other === acc && open);
          other.querySelector('.lx-acc__head').setAttribute('aria-expanded', String(other === acc && open));
        });
      });
    });

    /* ---------- Floating bar: shown once the main button has scrolled
       above the viewport, hidden again when it comes back. ---------- */
    const sticky = root.querySelector('[data-lx-sticky]');
    if (sticky && addBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-shown', passed);
        sticky.setAttribute('aria-hidden', String(!passed));
        if (stickyAdd) stickyAdd.setAttribute('tabindex', passed ? '0' : '-1');
      }).observe(addBtn);
    }
  }

  const boot = () => document.querySelectorAll('[data-lx-product]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-lx-product]').forEach(initProduct));
})();
